/**
 * Hybrid Layout
 *
 * Full-featured layout for sites with both ecommerce and content features.
 * Combines shopping functionality with content-focused navigation.
 */
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'

interface SiteConfig {
  name: string
  theme?: {
    primary_color?: string
    secondary_color?: string
  }
}

interface HybridLayoutProps {
  children: ReactNode
}

// Cache the site config to avoid repeated fetches
let cachedSiteConfig: SiteConfig | null = null

export function HybridLayout({ children }: HybridLayoutProps) {
  return (
    <div className="hybrid-layout min-h-screen flex flex-col">
      <HybridHeader />
      <main className="flex-1">{children}</main>
      <HybridFooter />
    </div>
  )
}

function HybridHeader() {
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(cachedSiteConfig)

  useEffect(() => {
    if (cachedSiteConfig) return

    fetch('/data/site-config.json')
      .then(res => res.json())
      .then(data => {
        cachedSiteConfig = data
        setSiteConfig(data)
      })
      .catch(() => {
        // Use default if fetch fails
        const defaultConfig = { name: 'Store' }
        cachedSiteConfig = defaultConfig
        setSiteConfig(defaultConfig)
      })
  }, [])

  const brandName = siteConfig?.name || 'Store'
  const primaryColor = siteConfig?.theme?.primary_color

  return (
    <header className="sticky top-0 z-50 bg-white border-b">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="font-bold text-xl" style={primaryColor ? { color: primaryColor } : undefined}>
            {brandName}
          </Link>

          {/* Main Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <Link to="/" className="text-gray-600 hover:text-gray-900 font-medium">
              Home
            </Link>
            <Link to={"/products" as any} className="text-gray-600 hover:text-gray-900 font-medium">
              Shop
            </Link>
            <Link to={"/blog" as any} className="text-gray-600 hover:text-gray-900 font-medium">
              Blog
            </Link>
            <Link to="/about" className="text-gray-600 hover:text-gray-900 font-medium">
              About
            </Link>
          </nav>

          {/* Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Search */}
            <button style={{ padding: '8px', borderRadius: '9999px' }} className="hover:bg-gray-100">
              <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>

            {/* Cart */}
            <Link to={"/cart" as any} style={{ padding: '8px', borderRadius: '9999px', position: 'relative' }} className="hover:bg-gray-100">
              <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </Link>

            {/* Mobile Menu */}
            <button style={{ padding: '8px', display: 'none' }} className="lg:block">
              <svg style={{ width: '24px', height: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </header>
  )
}

function HybridFooter() {
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(cachedSiteConfig)

  useEffect(() => {
    if (cachedSiteConfig) {
      setSiteConfig(cachedSiteConfig)
      return
    }

    fetch('/data/site-config.json')
      .then(res => res.json())
      .then(data => {
        cachedSiteConfig = data
        setSiteConfig(data)
      })
      .catch(() => {})
  }, [])

  const brandName = siteConfig?.name || 'Store'

  return (
    <footer className="bg-gray-900 text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <h3 className="font-bold text-xl mb-4 text-white">{brandName}</h3>
            <p className="text-gray-400 text-sm">
              Luxury furniture crafted with passion and precision.
            </p>
          </div>

          {/* Shop Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Shop</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="/products" className="hover:text-white transition-colors">All Products</a></li>
              <li><a href="/products?category=new" className="hover:text-white transition-colors">New Arrivals</a></li>
              <li><a href="/products?featured=true" className="hover:text-white transition-colors">Featured</a></li>
            </ul>
          </div>

          {/* Content Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Explore</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="/blog" className="hover:text-white transition-colors">Blog</a></li>
              <li><a href="/about" className="hover:text-white transition-colors">About Us</a></li>
              <li><a href="/contact" className="hover:text-white transition-colors">Contact</a></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-semibold text-white mb-4">Support</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="/shipping" className="hover:text-white transition-colors">Shipping Info</a></li>
              <li><a href="/returns" className="hover:text-white transition-colors">Returns & Refunds</a></li>
              <li><a href="/faq" className="hover:text-white transition-colors">FAQ</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} All rights reserved.</p>
        </div>
      </div>
    </footer>
  )
}

export default HybridLayout
